(ns io.daskul.webapp.widgets.fsm.transitions
  (:require
   [uix.core :as uix :refer [defui $]]
   [re-frame.core :as rf]
   [uix.re-frame :as urf]
   [io.daskul.webapp.ui :as ui]
   [clojure.string :as str]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.aggregates.subs :as agg.subs]
   [io.daskul.webapp.widgets.fsm.colors :as colors]
   [io.daskul.webapp.widgets.fsm.transition-logic :as tl]
   [io.daskul.webapp.widgets.fsm.badge :as badge]
   [io.daskul.webapp.widgets.form.field :as ff]
   [io.daskul.webapp.widgets.display :as display]))

;; ============================================================================
;; Copy rendering — :description / :hint
;; ============================================================================
;;
;; Both keys accept either a plain string (rendered as dimmed text, matching
;; the look every existing transition already has) or a map
;; {:message "..." :variant "info"} (rendered via ui.cljs's semantic alert
;; presets — info-alert/warning-alert/error-alert/success-alert, "single
;; source of truth for alert styling" per their own docstring, so this
;; doesn't hardcode :color/:variant on ui/alert directly). :variant reuses
;; the same "info"/"success"/"warning"/"danger" vocabulary as button
;; :variant elsewhere in this file — "danger" maps to error-alert (the
;; preset naming differs; colors.cljs already documents "danger" -> red).
;; "secondary"/"neutral" have no titled preset to reuse, so they fall back
;; to a plain gray ui/alert.
;;
;; :description = self-referential ("what this transition does").
;; :hint         = cross-referential ("consider this OTHER transition
;;                 instead"). Kept as a separate key because a transition
;;                 can want both shown at once (e.g. schedule-makeup explains
;;                 itself AND points at schedule-makeup-attach) — one field
;;                 can't hold two independent messages without conflating them.

(def ^:private variant->alert-preset
  {"info" ui/info-alert
   "warning" ui/warning-alert
   "danger" ui/error-alert
   "success" ui/success-alert})

(defn- render-copy
  "Render a :description/:hint value. `text-props` merges into the plain-text
   branch only, so each call site can match its existing size/spacing.
   Returns nil for nil (safe to call unconditionally)."
  ([value] (render-copy value {}))
  ([value text-props]
   (cond
     (nil? value) nil
     (map? value)
     (let [variant (or (:variant value) "info")]
       (if-let [preset (get variant->alert-preset variant)]
         ($ preset {:data-testid "fsm-transition-alert"} (:message value))
         ($ ui/alert {:color (colors/get-color variant) :variant "light"
                      :data-testid "fsm-transition-alert"}
            (:message value))))
     :else ($ ui/text (merge {:c "dimmed"} text-props) value))))

;; ============================================================================
;; Record-conditional alerts (#803)
;; ============================================================================

(def ^:private severity->alert-preset
  {"critical" ui/error-alert
   "warning" ui/warning-alert
   "info" ui/info-alert
   "note" ui/info-alert})

(defui transition-alerts
  "What will happen if you go through with this, given THIS record — resolved
   server-side from the transition's `:ui :alerts` (same declaration shape as
   F-059 record alerts).

   Above the fields rather than below, because the whole point is to be read
   before the form is filled: renew's warning is that no invoice will be
   written, and the field that could fix it is the one underneath."
  [{:keys [transition]}]
  (when-let [alerts (seq (get-in transition [:ui :alerts]))]
    ($ ui/stack {:gap "xs" :data-testid "fsm-transition-alerts"}
       (for [{:keys [id severity title body]} alerts
             :let [preset (get severity->alert-preset (name (or severity "info"))
                               ui/info-alert)]]
         ($ preset {:key (str id) :title title} body)))))

;; ============================================================================
;; Record-sourced context (#792)
;; ============================================================================

(defui transition-context
  "Read-only facts from the record, above the param form. Renders nothing when
   the transition declares no `:ui :context` or none of it resolves — see
   transition-logic/context-rows for why it exists."
  [{:keys [transition record]}]
  (when-let [rows (tl/context-rows transition record)]
    ($ ui/paper {:p "xs" :withBorder true :data-testid "fsm-transition-context"}
       ($ ui/stack {:gap 4}
          (for [{:keys [label value]} rows]
            ($ ui/group {:key label :gap "sm" :wrap "nowrap"}
               ($ ui/text {:size "xs" :c "dimmed"
                           :style {:minWidth "90px" :flexShrink 0}} label)
               ($ ui/text {:size "sm" :fw 500} value)))))))

;; ============================================================================
;; Transition Form Fields
;; ============================================================================

(defn extract-param-fields
  "Extract form field definitions from :params Malli schema or :form config.
   Returns vector of {:name :type :label :required} maps."
  [transition]
  (let [{:keys [params ui]} transition
        form-fields (get-in ui [:form :fields])]
    (cond
      ;; Explicit form fields in UI config
      (seq form-fields)
      form-fields

      ;; Resolved format from API: vector of maps with :name + :schema
      ;; e.g. [{:name "package-id" :schema "uuid" :ui {:widget "select" ...}}]
      (and (vector? params) (seq params) (map? (first params)) (:name (first params)))
      (mapv (fn [p]
              (cond-> {:name (keyword (:name p))
                       :schema (:schema p)
                       :required (not (:optional p))}
                (:ui p) (assoc :ui (:ui p))
                (some? (:default p)) (assoc :default (:default p))))
            params)

      ;; Legacy: Extract from Malli :params schema [:map [:field-name :type] ...]
      (and params (vector? params) (= :map (first params)))
      (->> (rest params)
           (filter vector?)
           (map (fn [[field-name field-opts]]
                  (let [opts (if (map? (second (if (vector? field-name) field-name [field-name])))
                               (second field-name)
                               {})
                        actual-name (if (vector? field-name) (first field-name) field-name)]
                    {:name actual-name
                     :type :text
                     :label (name actual-name)
                     :required (not (:optional opts))}))))

      :else nil)))

(defn- extract-defaults
  "Build initial form-data map from fields that have :default values."
  [fields]
  (reduce (fn [acc {:keys [name default]}]
            (if (some? default)
              (assoc acc name default)
              acc))
          {} fields))

(defui transition-form-field
  "Renders a single form field for transition params.
   Delegates to ff/form-field for proper widget rendering (select, date, etc.).
   Merges record into form-data so depends-on resolution finds parent values."
  [{:keys [agg-key field value on-change record form-data error]}]
  (if (:schema field)
    ;; Resolved format: delegate to form-field (handles select, date, textarea, etc.)
    ($ ff/form-field
       {:agg-key agg-key
        :field field
        :value value
        :on-change on-change
        :external-error error
        :form-data (merge record form-data)})
    ;; Legacy format (no :schema): plain text input
    (let [field-name (:name field)
          label (:label field)
          required (:required field)]
      ($ ui/text-input
         {:label (or label (str/capitalize (name field-name)))
          :value (or value "")
          :onChange #(on-change (.. % -target -value))
          :required required
          :error error}))))

;; ============================================================================
;; Transition Button Component
;; ============================================================================

(defui transition-button
  "Renders a single transition action button with optional confirmation/form dialog.

   Props:
     transition - Transition map with :event, :ui, and optional :params
     on-click   - Callback when transition is confirmed (receives event name, params)
     loading?   - Whether the transition is in progress

   When :ui contains :confirm, shows a modal dialog before executing.
   When :params is defined, shows form fields in the modal.
   Confirm config: {:title \"...\" :message \"...\" :confirm-label \"...\" :cancel-label \"...\"}

   Example:
     ($ transition-button
       {:transition {:event \"discard\"
                     :params [:map [:reason :string]]
                     :ui {:label \"Discard\" :variant \"danger\"
                          :confirm {:title \"Discard?\"
                                    :message \"This cannot be undone.\"}}}
        :on-click (fn [event params] (dispatch-transition event params))
        :loading? false})"
  [{:keys [transition on-click loading? agg-key record size data-testid]}]
  (let [{:keys [event ui params]} transition
        {:keys [label description icon variant confirm]} ui
        display-label (or label event)
        ;; A tooltip can only host plain text, not the alert render-copy
        ;; produces elsewhere for map-shaped :description — pull the string
        ;; out either way (string as-is, map's :message, else nil).
        description-text (cond
                            (string? description) description
                            (map? description) (:message description)
                            :else nil)
        ;; The launching button competes with its siblings, so it takes the
        ;; declared variant; the confirm inside the modal does not (#792).
        button-props (colors/get-button-props variant)
        confirm-props (tl/confirm-button-props transition)
        [modal-open? set-modal-open!] (uix/use-state false)
        [form-data set-form-data!] (uix/use-state {})
        transition-errors (urf/use-subscribe [::agg.subs/transition-errors agg-key])
        transitioning? (urf/use-subscribe [::agg.subs/transitioning? agg-key])
        field-errors (:field-errors transition-errors)

        ;; loading? prop matches event name, but only spin while actively transitioning
        actually-loading? (and loading? transitioning?)

        ;; Show modal if has confirm OR has params
        form-fields (extract-param-fields transition)
        form-field-names (into #{} (map :name) form-fields)
        has-params? (seq form-fields)
        has-confirm? (some? confirm)
        needs-modal? (or has-confirm? has-params?)

        ;; Field errors that don't match any transition form field (e.g., from tx-handlers)
        unmatched-errors (when (seq field-errors)
                           (into {} (remove #(form-field-names (key %))) field-errors))

        handle-click (fn []
                       (if needs-modal?
                         (do
                           (set-form-data! (extract-defaults form-fields))
                           (set-modal-open! true))
                         (on-click event nil)))

        ;; Close the modal only AFTER a transition we started completes (loading
        ;; went true→false, no errors). Guarding on the prior loading state stops
        ;; the modal from closing the instant it opens — idle (not-loading,
        ;; no-errors) would otherwise satisfy a bare close condition. Mirrors the
        ;; was-transitioning? pattern in transition-action-panel below.
        was-transitioning? (uix/use-ref false)
        _ (uix/use-effect
           (fn []
             (if actually-loading?
               (reset! was-transitioning? true)
               (when (and @was-transitioning? modal-open? (nil? transition-errors))
                 (reset! was-transitioning? false)
                 (set-modal-open! false)))
             js/undefined)
           [actually-loading? modal-open? transition-errors])

        ;; See expanded-panel: hidden fields keep their value, but never travel.
        shown-fields (tl/visible-fields form-fields form-data)

        handle-confirm (fn []
                         (on-click event (when has-params?
                                           (tl/submitted-params form-fields form-data))))]

    ($ :<>
      ;; The button
       ($ ui/tooltip
          {:label (or description-text display-label)
           :withArrow true
           :disabled (not description-text)}
          ($ ui/button
             (merge button-props
                    {:onClick handle-click
                     :loading actually-loading?
                     :leftSection (badge/get-icon icon 16)
                     :size (or size "sm")
                     :data-testid data-testid})
             display-label))

      ;; Modal for confirmation and/or form fields
       (when needs-modal?
         ($ ui/modal
            {:opened modal-open?
             :onClose #(set-modal-open! false)
             :title (or (:title confirm) display-label)
             :centered true}
            ($ ui/stack {:gap "md"}
            ;; Confirmation message
               (when (:message confirm)
                 ($ ui/text (or (:message confirm) "")))

            ;; Error alert — only when errors can't be shown inline on form fields:
               ;; - Unmatched field errors (from tx-handlers on other aggregates)
               ;; - Plain message with no field errors (business rule violations)
               ;; When all field errors match form fields, they show inline — no alert needed.
               (let [has-matched? (seq (filter #(form-field-names (key %)) field-errors))
                     show-alert? (or (seq unmatched-errors)
                                     (and (:message transition-errors) (not has-matched?)))]
                 (when show-alert?
                   (let [detail-lines (map (fn [[k v]] (str (display/humanize-field-name k) " " v)) unmatched-errors)
                         body (if (seq detail-lines)
                                (str/join "\n" detail-lines)
                                (:message transition-errors))]
                     ($ ui/alert {:color "red" :variant "light" :title "Error"}
                        body))))

            ;; What this action will do given THIS record, then the
            ;; record-sourced context, both above the fields they explain
               ($ transition-alerts {:transition transition})
               ($ transition-context {:transition transition :record record})

            ;; Form fields for params
               (when has-params?
                 ($ ui/stack {:gap "sm"}
                    (for [field shown-fields]
                      (let [field-name (:name field)]
                        ($ transition-form-field
                           {:key (name field-name)
                            :agg-key agg-key
                            :field field
                            :value (get form-data field-name)
                            :on-change (fn [v]
                                         (set-form-data! (assoc form-data field-name v))
                                         (when (get field-errors field-name)
                                           (rf/dispatch [::agg.events/clear-transition-field-error agg-key field-name])))
                            :record record
                            :form-data form-data
                            :error (get field-errors field-name)})))))

            ;; Action buttons
               ($ ui/group {:justify "flex-end" :gap "sm"}
                  ($ ui/button
                     {:variant "subtle" :color "gray"
                      :onClick #(set-modal-open! false)}
                     (or (:cancel-label confirm) "Cancel"))
                  ($ ui/button
                     (merge confirm-props
                            {:onClick handle-confirm
                             :loading actually-loading?})
                     (or (:confirm-label confirm) "Confirm")))))))))

;; ============================================================================
;; Transition Action — self-contained FSM trigger (button + derived modal)
;; ============================================================================

(defui transition-action
  "Self-contained FSM transition trigger for a single record.

   Give it a `:transition` (a def from the record's `_meta.fsm.transitions`,
   carrying `:event` / `:params` / `:ui`), the `:aggregate` type keyword, and the
   record's `:record-id`, and it owns the whole lifecycle: it DERIVES whether the
   transition needs input (`:params` / `:ui :confirm`) and, if so, opens a modal
   with the param form; otherwise it fires immediately. It POSTs
   `/<agg>/<id>/transition`, shows the loading spinner, closes on success, and
   surfaces validation errors inline in the modal. Label / variant / icon come
   from the transition's `:ui` (single source of truth).

   Optional callbacks fire after the transition settles — for surface refreshes
   the framework can't infer (a parent chip, a sibling list):
     :on-success (fn [record])   — after a successful transition, with the
                                   transitioned record
     :on-failure (fn [errors])   — after a failed transition, with parsed errors

   Optional presentation overrides: :size, :data-testid.

   This is the list-row counterpart to `transition-action-panel` (the two-phase
   inline panel used for a single focal entity in a sidebar)."
  [{:keys [transition aggregate record-id on-success on-failure size data-testid]}]
  (let [event (:event transition)
        transition-event (urf/use-subscribe [::agg.subs/transition-event aggregate])
        on-click (uix/use-callback
                  (fn [ev params]
                    (rf/dispatch [::agg.events/transition aggregate record-id ev params
                                  {:on-success on-success :on-failure on-failure}]))
                  [aggregate record-id on-success on-failure])]
    ($ transition-button
       {:transition transition
        :agg-key aggregate
        :on-click on-click
        :loading? (= event transition-event)
        :size size
        :data-testid data-testid})))

;; ============================================================================
;; Transitions Panel - Buttons Mode (default)
;; ============================================================================

(defui transitions-buttons
  "Renders transitions as a row of buttons (default mode).
   on-transition callback receives (event params)."
  [{:keys [transitions on-transition loading-event agg-key record]}]
  ($ ui/group {:gap "sm"}
     (for [transition transitions]
       ($ transition-button
          {:key (:event transition)
           :transition transition
           :on-click (fn [event params] (on-transition event params))
           :loading? (= (:event transition) loading-event)
           :agg-key agg-key
           :record record}))))

;; ============================================================================
;; Transitions Panel - Tabs Mode
;; ============================================================================

(defui transition-tab-content
  "Content for a single transition tab - shows details and action button.
   on-transition callback receives (event params)."
  [{:keys [transition on-transition loading? agg-key record]}]
  (let [{:keys [ui]} transition
        {:keys [description]} ui]
    ($ ui/stack {:gap "md" :p "md"}
      ;; Description if available
       (render-copy description)
      ;; Action button
       ($ transition-button
          {:transition transition
           :on-click (fn [event params] (on-transition event params))
           :loading? loading?
           :agg-key agg-key
           :record record}))))

(defui transitions-tabs
  "Renders transitions as tabs with details in each tab panel."
  [{:keys [transitions on-transition loading-event agg-key record]}]
  (let [first-event (:event (first transitions))]
    ($ ui/tabs {:defaultValue first-event}
      ;; Tab list
       ($ ui/tabs-list
          (for [transition transitions]
            (let [{:keys [event ui]} transition
                  {:keys [label icon variant]} ui
                  display-label (or label event)
                  color (colors/get-color variant)]
              ($ ui/tabs-tab
                 {:key event
                  :value event
                  :leftSection (badge/get-icon icon 16)
                  :color color}
                 display-label))))
      ;; Tab panels
       (for [transition transitions]
         ($ ui/tabs-panel
            {:key (:event transition)
             :value (:event transition)}
            ($ transition-tab-content
               {:transition transition
                :on-transition on-transition
                :loading? (= (:event transition) loading-event)
                :agg-key agg-key
                :record record}))))))

;; ============================================================================
;; Transitions Panel Component
;; ============================================================================

(defui transitions-panel
  "Renders available FSM transitions based on view config.

   Props:
     transitions      - Vector of available transition maps
     view             - View config {:mode \"buttons\"|\"tabs\", :title \"...\", :description \"...\"}
     on-transition    - Callback when transition is clicked (receives event name)
     loading-event    - Event name currently being processed (or nil)

   View modes:
     - \"buttons\" (default) - Simple row of action buttons
     - \"tabs\" - Tabbed interface with details in each panel

   Example:
     ($ transitions-panel
       {:transitions [{:event \"publish\" :ui {:label \"Publish\" :variant \"success\"}}]
        :view {:mode \"tabs\" :title \"Actions\" :description \"Choose an action\"}
        :on-transition #(rf/dispatch [::events/transition agg-key id %])
        :loading-event nil})"
  [{:keys [transitions view on-transition loading-event agg-key record]}]
  (when (seq transitions)
    (let [{:keys [mode title description]} view
          view-mode (or mode "buttons")]
      ($ ui/stack {:gap "sm"}
        ;; Title and description
         (when (or title description)
           ($ ui/box
              (when title
                ($ ui/text {:fw 500 :size "sm"} title))
              (when description
                ($ ui/text {:c "dimmed" :size "sm"} description))))
        ;; Transitions based on mode
         (case view-mode
           "tabs" ($ transitions-tabs
                     {:transitions transitions
                      :on-transition on-transition
                      :loading-event loading-event
                      :agg-key agg-key
                      :record record})
          ;; Default: buttons
           ($ transitions-buttons
              {:transitions transitions
               :on-transition on-transition
               :loading-event loading-event
               :agg-key agg-key
               :record record}))))))

;; ============================================================================
;; Transition Action Panel - Two-Phase Mode (for sidebar)
;; ============================================================================
;;
;; Phase 1 (Choose): Stacked buttons with visual hierarchy. Nothing expanded.
;; Phase 2 (Fill):   Inline expansion below buttons with form fields + confirm.

(defui expanded-panel
  "Phase 2: Inline expansion showing form fields and confirm/cancel buttons.
   Stays open on validation failure, shows field-level errors inline.

   `heading?` (default true) renders the action's label above the copy. A
   popover has no chrome of its own, so it needs it. A drawer already puts
   the same label in its header, and rendering both showed `Schedule New
   Trial` twice, one line apart.

   `container` (default :inline) is the host the panel is rendered into, and
   it decides whether `:ui :confirm` becomes a nested modal or is absorbed
   into the copy — see transition-logic/nested-confirm? for why a popover
   cannot hold one."
  [{:keys [transition on-transition on-cancel loading? agg-key record heading?
           container]
    :or {heading? true container :inline}}]
  (let [{:keys [event ui]} transition
        {:keys [label confirm]} ui
        display-label (or label event)
        confirm-props (tl/confirm-button-props transition)
        form-fields (extract-param-fields transition)
        form-field-names (into #{} (map :name) form-fields)
        has-params? (seq form-fields)
        has-confirm-modal? (tl/nested-confirm? container transition)
        [form-data set-form-data!] (uix/use-state (extract-defaults form-fields))
        [confirm-open? set-confirm-open!] (uix/use-state false)
        transition-errors (urf/use-subscribe [::agg.subs/transition-errors agg-key])
        field-errors (:field-errors transition-errors)
        unmatched-errors (when (seq field-errors)
                           (into {} (remove #(form-field-names (key %))) field-errors))

        ;; Fields whose :visible-when no longer holds are dropped from the
        ;; render but KEPT in form-data, so toggling the condition back on
        ;; restores what was picked. They are stripped at submit instead.
        shown-fields (tl/visible-fields form-fields form-data)

        handle-execute (fn []
                         (set-confirm-open! false)
                         (on-transition event (when has-params?
                                                (tl/submitted-params form-fields form-data))))

        handle-confirm-click (fn []
                               (if has-confirm-modal?
                                 (set-confirm-open! true)
                                 (handle-execute)))]

    ;; Clear stale lookup options for autocomplete fields on mount
    (uix/use-effect
     (fn []
       (doseq [field form-fields
               :when (= :autocomplete (keyword (get-in field [:ui :widget])))]
         (rf/dispatch-sync [::agg.events/clear-lookup-options agg-key (:name field)]))
       js/undefined)
     [agg-key form-fields])

    ($ ui/stack {:gap "sm" :data-testid "fsm-transition-form"}
      ;; Title — suppressed when the container already shows it (drawer).
      (when heading?
        ($ ui/text {:fw 500 :size "sm"} display-label))

      ;; Description, plus the `:confirm` message when no modal will carry it.
      ;; Each value is whatever the definition authored — a string or an alert
      ;; map — so render-copy still decides how to draw it.
       (for [[i copy] (map-indexed vector (tl/panel-copy container transition))]
         ($ :<> {:key i} (render-copy copy {:size "sm"})))

      ;; :ui :hint (if present) is shown at Phase 1 (transition-action-panel,
      ;; before the user commits to this action) — not repeated here, so the
      ;; nudge toward an alternative isn't shown again after they've already
      ;; chosen.

      ;; Error alert — same logic as modal variant (see comment there)
       (let [has-matched? (seq (filter #(form-field-names (key %)) field-errors))
             show-alert? (or (seq unmatched-errors)
                             (and (:message transition-errors) (not has-matched?)))]
         (when show-alert?
           (let [detail-lines (map (fn [[k v]] (str (display/humanize-field-name k) " " v)) unmatched-errors)
                 body (if (seq detail-lines)
                        (str/join "\n" detail-lines)
                        (:message transition-errors))]
             ($ ui/alert {:color "red" :variant "light" :title "Error"}
                body))))

      ;; What this action will do given THIS record, then the record-sourced
      ;; context, both above the fields they explain
       ($ transition-alerts {:transition transition})
       ($ transition-context {:transition transition :record record})

      ;; Form fields (if transition has params)
       (when has-params?
         ($ ui/stack {:gap "sm"}
            (for [field shown-fields]
              (let [field-name (:name field)]
                ($ transition-form-field
                   {:key (name field-name)
                    :agg-key agg-key
                    :field field
                    :value (get form-data field-name)
                    :on-change (fn [v]
                                 (set-form-data! (assoc form-data field-name v))
                                 (when (get field-errors field-name)
                                   (rf/dispatch [::agg.events/clear-transition-field-error agg-key field-name])))
                    :record record
                    :form-data form-data
                    :error (get field-errors field-name)})))))

      ;; Cancel + Confirm. `mt` on top of the stack's own gap — the commit row
      ;; is a different kind of thing from the fields above it, and reading as
      ;; one more row of the form is what made it easy to miss.
       ($ ui/group {:justify "flex-end" :gap "sm" :mt "md"}
          ($ ui/button
             {:variant "subtle" :color "gray" :size "sm" :onClick on-cancel}
             "Cancel")
          ($ ui/button
             (merge confirm-props
                    {:size "sm" :loading loading? :onClick handle-confirm-click})
             display-label))

      ;; Confirmation modal (for dangerous transitions with :confirm config)
       (when has-confirm-modal?
         ($ ui/modal
            {:opened confirm-open?
             :onClose #(set-confirm-open! false)
             :title (or (:title confirm) (str display-label "?"))
             :centered true}
            ($ ui/stack {:gap "md"}
               (when (:message confirm)
                 ($ ui/text (:message confirm)))
               ($ ui/group {:justify "flex-end" :gap "sm"}
                  ($ ui/button
                     {:variant "subtle" :color "gray" :onClick #(set-confirm-open! false)}
                     (or (:cancel-label confirm) "Cancel"))
                  ($ ui/button
                     (merge confirm-props {:onClick handle-execute})
                     (or (:confirm-label confirm) "Confirm")))))))))

(defui transition-drawer
  "Drawer host for a transition whose declared fields earn one (F-061): any
   required param. Width comes from content, not from a per-transition setting.

   The body is `expanded-panel` unchanged — it already owns the param form,
   defaults, field errors and the confirm modal, so this only decides where
   that lives.

   There is deliberately no popover variant here. A popover must WRAP its
   target to be positioned, so it belongs where the launching button is
   rendered; hosting one as a detached sibling puts it in the top-left corner
   of the page, which is exactly what happened when this tried."
  [{:keys [transition on-transition on-cancel loading? agg-key record
           opened on-exited]}]
  ;; ALWAYS MOUNTED, `opened` controlled.
  ;;
  ;; Mantine's Transition renders in its entered state when it mounts already
  ;; open, so a drawer that only mounts once a transition is chosen can never
  ;; animate in — measured zero `transitionstart` events (#705). Deferring the
  ;; flip by an effect does not help: the change lands in the same commit, so
  ;; no closed frame is ever painted.
  ;;
  ;; Mounting it up front and toggling `opened` gives the transition both a
  ;; start state and, on the way out, somewhere to animate TO — the exit was
  ;; equally dead before, since cancelling unmounted the component outright.
  ;;
  ;; `transition` is therefore nil whenever the drawer is closed, and stays
  ;; non-nil through the exit so the body does not blank mid-slide.
  (let [fields (extract-param-fields transition)]
    ($ ui/drawer
      {:opened (boolean opened)
       :transitionProps #js {:onExited (fn [] (when on-exited (on-exited)))}
       :onClose on-cancel
       :position "right"
       :size (tl/drawer-width fields)
       :title (get-in transition [:ui :label])
       ;; Escape does NOT discard the drawer, and that asymmetry with the
       ;; confirm popover is the point: a popover has nothing to lose, so
       ;; Escape dismisses it; a drawer holds a part-filled form.
       ;;
       ;; It is also the only sane reading of the keystroke here. Every roomy
       ;; field in this form — date, time, select — opens a dropdown of its
       ;; own, and Escape while one is open means "close this dropdown", not
       ;; "throw away everything I typed". Mantine fires both, innermost
       ;; first, so leaving this on cost the user the whole form on one
       ;; keypress. Cancel and the header ✕ are the ways out.
       :closeOnEscape false
       :data-testid "transition-drawer"}
      ($ expanded-panel
        {:transition transition
         :on-transition on-transition
         :on-cancel on-cancel
         :loading? loading?
         :agg-key agg-key
         :record record
         :container :drawer
         ;; The Drawer header is already the label.
         :heading? false}))))

(defui transition-action-panel
  "Two-phase transition panel for sidebar layout.

   Phase 1: Stacked buttons with visual hierarchy (first=filled, rest=subtle).
   Phase 2: Clicking a button expands inline form + confirm below.

   Props:
     transitions      - Vector of available transition maps
     on-transition    - Callback when transition is confirmed (receives event, params)
     loading-event    - Event name currently being processed (or nil)
     agg-key          - Aggregate keyword for form field resolution
     record           - Full aggregate record
     transitioning?   - Optional override for transitioning state (for child aggregates
                        where the subscription path differs from the top-level aggregate)"
  [{:keys [transitions on-transition loading-event agg-key record] :as props}]
  (let [[expanded-event set-expanded!] (uix/use-state nil)
        sub-transitioning? (urf/use-subscribe [::agg.subs/transitioning? agg-key])
        transitioning? (if (contains? props :transitioning?)
                         (:transitioning? props)
                         sub-transitioning?)
        transition-errors (urf/use-subscribe [::agg.subs/transition-errors agg-key])
        was-transitioning? (uix/use-ref false)

        ;; Reset expansion when transitions change (e.g., after state transition)
        _ (uix/use-effect
           (fn []
             (when (and expanded-event
                        (not (some #(= (:event %) expanded-event) transitions)))
               (set-expanded! nil))
             js/undefined)
           [transitions expanded-event])

        ;; Auto-collapse on successful transition (including self-transitions)
        _ (uix/use-effect
           (fn []
             (if transitioning?
               (reset! was-transitioning? true)
               (when (and @was-transitioning?
                          expanded-event
                          (nil? transition-errors))
                 (reset! was-transitioning? false)
                 (set-expanded! nil)))
             js/undefined)
           [transitioning? expanded-event transition-errors])

        expanded-transition (when expanded-event
                              (some #(when (= (:event %) expanded-event) %)
                                    transitions))]

    (when (seq transitions)
      ($ ui/stack {:gap "sm"}
         (if-not expanded-event
          ;; Phase 1: Choose — show buttons
           (let [first-actionable? (get-in (first transitions) [:ui :actionable] true)
                 ;; Secondary = all except first, filtered to actionable only
                 secondary (filterv #(get-in % [:ui :actionable] true) (rest transitions))]
             ($ :<>
              ;; Primary action (first transition) — full-width outline button
              ;; Skip when :actionable false (alert shown by sidebar instead)
                (when first-actionable?
                  (let [{:keys [event ui]} (first transitions)
                        display-label (or (:label ui) event)]
                    ($ :<>
                       ;; Discoverability hint (:ui :hint) — shown ONLY at
                       ;; Phase 1, before the user commits to expanding this
                       ;; action, so it can steer them to a sibling action
                       ;; (e.g. "More actions") instead. Above the button so
                       ;; it's seen before the click, not repeated in the
                       ;; expanded panel (Phase 2) — once they've already
                       ;; chosen, re-nudging toward the alternative is noise.
                       (render-copy (:hint ui) {:size "sm"})
                       ($ ui/button
                          (merge (colors/get-button-props (:variant ui))
                                 {:variant "outline"
                                  :size "sm"
                                  :fullWidth true
                                  :onClick #(set-expanded! event)})
                          display-label))))

              ;; Secondary actions — "More actions" dropdown menu.
              ;; The app bundle doesn't ship @mantine/core's Popover/Menu CSS, so
              ;; the dropdown surface (bg/border/shadow/radius) must be set
              ;; inline — otherwise it renders transparent and the menu items
              ;; bleed over whatever card sits below it (SMS-151).
                (when (seq secondary)
                  ($ ui/menu {:position "bottom-end" :withArrow true :shadow "md"
                              :zIndex 400
                              :styles #js {:dropdown
                                           #js {:minWidth "min(300px, calc(100vw - 2rem))"
                                                :background "var(--mantine-color-body, #fff)"
                                                :border "1px solid var(--mantine-color-gray-3, #dee2e6)"
                                                :borderRadius "var(--mantine-radius-md, 8px)"
                                                :boxShadow "0 4px 16px rgba(43,28,60,.16)"
                                                :padding "4px"}}}
                     ($ ui/menu-target
                        ($ ui/button
                           {:variant "default"
                            :color "gray"
                            :size "sm"
                            :fullWidth true}
                           "More actions ▾"))
                     ($ ui/menu-dropdown
                        (for [t secondary
                              :let [{:keys [event ui]} t
                                    display-label (or (:label ui) event)
                                    description (:description ui)
                                    icon (:icon ui)
                                    color (colors/get-color (:variant ui))]]
                          ($ ui/menu-item
                             (cond-> {:key event
                                      :onClick #(set-expanded! event)}
                               icon (assoc :leftSection
                                           ($ ui/action-icon
                                              {:variant "light"
                                               :color color
                                               :radius "xl"
                                               :size "lg"}
                                              (badge/get-icon icon 18))))
                             ($ ui/stack {:gap 2}
                                ($ ui/text {:size "sm" :fw 500} display-label)
                                (render-copy description {:size "xs"})))))))))

          ;; Phase 2: Fill — panel stays open until success (errors show inline)
          ;; loading? requires both event match AND actively transitioning
          ;; (transition-event persists on failure to keep panel open, but transitioning? clears)
           ($ expanded-panel
              {:transition expanded-transition
               :on-transition on-transition
               :on-cancel #(set-expanded! nil)
               :loading? (and (= expanded-event loading-event) transitioning?)
               :agg-key agg-key
               :record record}))))))
